/**
 * Thẻ "Mục tiêu & món trọng điểm": mục tiêu doanh thu tháng của từng tiểu đội
 * và tối đa 10 món trọng điểm. Trưởng phòng KD nhập trước hạn của tháng (ngày 1;
 * riêng tháng đầu cơ chế theo `lateEntry` — CEO 18a), quá hạn chỉ còn xem. Giám
 * đốc, quản trị sửa được cả sau hạn (CEO 02/11/2026); máy chủ gác.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { MoneyInput } from "@/components/MoneyInput";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { formatVND } from "@/lib/format";
import { useProductCatalog } from "@/features/products/catalogApi";
import { useSurchargeCatalog } from "@/features/products/surchargeApi";
import { useServiceImageOf } from "@/features/cskh/tabs/bookingServiceImage";
import {
  type SalesKeyItemInput,
  type SalesOrgView,
  useSalesGnpmProducts,
  useSalesKeyItems,
  useSalesTargets,
  useSaveKeyItems,
  useSaveTargets,
} from "../api";
import { dayText, entryLastDay, monthText } from "../labels";
import { squadsToEnter } from "../helpers";
import { gnpfOptions, gnpmOptions, itemRefOf, type KeySystem } from "../keyItemOptions";
import { KeyItemPicker } from "./KeyItemPicker";
import { SquadMark } from "./marks";

const MAX_KEY_ITEMS = 10;

// manual = món GNPM chưa có trong danh sách, gõ tay mã + tên.
type KeyRow = { system: KeySystem; code: string; name: string; manual?: boolean };

function toRows(items: { itemRef: string; itemName: string }[]): KeyRow[] {
  return items.map((k) => {
    const [system, ...rest] = k.itemRef.split(":");
    return { system: system === "gnpm" ? "gnpm" : "gnpf", code: rest.join(":"), name: k.itemName };
  });
}

type Props = { org: SalesOrgView; month: string };

export function TargetsTab({ org, month }: Props) {
  const targets = useSalesTargets(month);
  const keyItems = useSalesKeyItems(month);
  const saveTargets = useSaveTargets(month);
  const saveKeys = useSaveKeyItems(month);
  const squads = useMemo(
    () => squadsToEnter(org, month, new Set((targets.data ?? []).map((t) => t.squadId))),
    [org, month, targets.data],
  );
  const lastDay = entryLastDay(month, org.lateEntry);
  const open = org.today <= lastDay;
  const canEdit = org.me.canSetTargets && (open || org.me.canEditPastDeadline);
  const teamOf = (parentId: string) => org.units.find((u) => u.id === parentId)?.name ?? "";

  // Số người đang gõ đè lên số máy chủ trả — tải xong muộn hay tải lại cũng
  // không xoá chữ đang gõ dở.
  const [draft, setDraft] = useState<Record<string, number | null>>({});
  const server = useMemo(() => {
    const out: Record<string, number> = {};
    for (const t of targets.data ?? []) out[t.squadId] = t.targetVnd;
    return out;
  }, [targets.data]);
  const valueOf = (squadId: string): number | null => (squadId in draft ? (draft[squadId] ?? null) : (server[squadId] ?? null));
  const [edited, setRows] = useState<KeyRow[] | null>(null);
  const rows = edited ?? toRows(keyItems.data ?? []);
  const catalog = useProductCatalog();
  const surcharges = useSurchargeCatalog();
  const gnpm = useSalesGnpmProducts(canEdit);
  const imageOf = useServiceImageOf();
  const options = useMemo(
    () => ({
      gnpf: gnpfOptions(catalog.products, surcharges.catalog.items, imageOf),
      gnpm: gnpmOptions(gnpm.data ?? []),
    }),
    [catalog.products, surcharges.catalog.items, gnpm.data, imageOf],
  );
  const patchRow = (i: number, patch: Partial<KeyRow>) =>
    setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const submitTargets = () => {
    const items = squads
      .filter((s) => (valueOf(s.id) ?? 0) > 0)
      .map((s) => ({ squadId: s.id, targetVnd: valueOf(s.id) as number }));
    saveTargets.mutate(items, {
      onSuccess: () => {
        setDraft({});
        toast.success("Đã lưu mục tiêu tháng");
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Không lưu được mục tiêu"),
    });
  };

  const submitKeys = () => {
    const items: SalesKeyItemInput[] = rows
      .filter((r) => r.code.trim() !== "" || r.name.trim() !== "")
      .map((r) => ({ itemRef: `${r.system}:${r.code.trim()}`, itemName: r.name.trim() }));
    saveKeys.mutate(items, {
      onSuccess: () => {
        setRows(null);
        toast.success("Đã lưu món trọng điểm");
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Không lưu được món trọng điểm"),
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted">
        {open
          ? `Tháng ${monthText(month)}: nhập được tới hết ngày ${dayText(lastDay)}.`
          : org.me.canEditPastDeadline
            ? `Tháng ${monthText(month)} đã qua hạn ngày ${dayText(lastDay)}, giám đốc và quản trị vẫn sửa được. Số mới áp cho cả tháng và được ghi lại trong nhật ký.`
            : `Tháng ${monthText(month)} đã khoá mục tiêu và món trọng điểm (hạn hết ngày ${dayText(lastDay)}).`}
        {!org.me.canSetTargets ? " Chỉ trưởng phòng Kinh doanh và giám đốc nhập được." : null}
      </p>

      <section aria-labelledby="muc-tieu" className="space-y-3">
        <h3 id="muc-tieu" className="text-base font-semibold">
          Mục tiêu doanh thu tháng
        </h3>
        {targets.isError ? (
          <QueryErrorNote error={targets.error} fallback="Không tải được mục tiêu tháng" />
        ) : squads.length === 0 ? (
          <p className="text-sm text-muted">Tháng này chưa có tiểu đội nào trong cơ cấu HRM gửi.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border bg-surface">
            {squads.map((s) => (
              <li key={s.id} className="flex flex-col gap-2 p-3 md:flex-row md:items-center md:justify-between">
                <SquadMark squadId={s.id} name={s.name} size={28} className="font-medium">
                  {s.name}
                  {/* Tiểu đội trùng tên ở hai đội: ghi tên đội để khỏi nhập nhầm. */}
                  {teamOf(s.parentId) ? <span className="ml-1 text-xs font-normal text-muted">· {teamOf(s.parentId)}</span> : null}
                </SquadMark>
                {canEdit ? (
                  <MoneyInput
                    aria-label={`Mục tiêu tháng của ${s.name}`}
                    value={valueOf(s.id)}
                    onChange={(v) => setDraft((d) => ({ ...d, [s.id]: v }))}
                    className="min-w-0 md:w-56"
                  />
                ) : (
                  <span className="tabular-nums">
                    {valueOf(s.id) ? formatVND(valueOf(s.id) as number) : "Chưa đặt"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && squads.length > 0 ? (
          <Button onClick={submitTargets} disabled={saveTargets.isPending}>
            {saveTargets.isPending ? "Đang lưu…" : "Lưu mục tiêu"}
          </Button>
        ) : null}
      </section>

      <section aria-labelledby="mon-trong-diem" className="space-y-3">
        <h3 id="mon-trong-diem" className="text-base font-semibold">
          Món trọng điểm (tối đa {MAX_KEY_ITEMS})
        </h3>
        {keyItems.isError ? (
          <QueryErrorNote error={keyItems.error} fallback="Không tải được món trọng điểm" />
        ) : canEdit ? (
          <div className="space-y-2">
            {rows.map((r, i) => {
              const key = r.code.trim() ? itemRefOf(r.system, r.code) : "";
              const taken = new Set(
                rows.filter((x, j) => j !== i && x.code.trim()).map((x) => itemRefOf(x.system, x.code)),
              );
              const list = options[r.system];
              const showManual = r.manual || (r.system === "gnpm" && !gnpm.isLoading && list.length === 0);
              return (
                <div key={i} className="grid grid-cols-1 gap-2 rounded-md border border-border bg-surface p-3 md:grid-cols-[8rem_1fr_auto]">
                  <select
                    aria-label={`Hệ của món ${i + 1}`}
                    value={r.system}
                    // Đổi hệ là bỏ món đang chọn: mã của hệ này không có nghĩa ở hệ kia.
                    onChange={(e) => patchRow(i, { system: e.target.value as KeySystem, code: "", name: "", manual: false })}
                    className="min-h-11 rounded-md border border-border bg-white px-2"
                  >
                    <option value="gnpf">GNPF</option>
                    <option value="gnpm">GNPM</option>
                  </select>
                  <div className="min-w-0 space-y-1">
                    {showManual ? (
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-[12rem_1fr]">
                        <Input
                          aria-label={`Mã món ${i + 1}`}
                          placeholder="Mã món"
                          value={r.code}
                          onChange={(e) => patchRow(i, { code: e.target.value })}
                        />
                        <Input
                          aria-label={`Tên món ${i + 1}`}
                          placeholder="Tên món"
                          value={r.name}
                          onChange={(e) => patchRow(i, { name: e.target.value })}
                        />
                      </div>
                    ) : (
                      <KeyItemPicker
                        index={i + 1}
                        options={list}
                        taken={taken}
                        selectedKey={key}
                        selectedName={r.name}
                        selectedImage={r.system === "gnpf" && key ? imageOf(r.code.trim()) : undefined}
                        loading={r.system === "gnpm" ? gnpm.isLoading : catalog.isLoading}
                        allowManual={r.system === "gnpm"}
                        onPick={(o) => patchRow(i, { code: o.code, name: o.label, manual: false })}
                        onManual={() => patchRow(i, { manual: true })}
                      />
                    )}
                    <div className="text-xs text-muted">
                      {r.system === "gnpm" && gnpm.isError ? (
                        <QueryErrorNote error={gnpm.error} fallback="Không tải được danh sách món GNPM" />
                      ) : showManual ? (
                        r.system === "gnpm" && list.length === 0
                          ? "GNPM chưa gửi sang món nào — gõ đúng mã món bên GNPM."
                          : "Gõ đúng mã món bên GNPM — sai một chữ là món không được tính."
                      ) : key ? (
                        `Mã: ${key}`
                      ) : null}
                      {r.manual && list.length > 0 ? (
                        <button
                          type="button"
                          className="ml-2 text-primary underline"
                          onClick={() => patchRow(i, { manual: false, code: "", name: "" })}
                        >
                          Chọn từ danh sách
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <Button type="button" variant="outline" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                    Bỏ món
                  </Button>
                </div>
              );
            })}
            <div className="flex flex-wrap gap-2">
              {rows.length < MAX_KEY_ITEMS ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRows([...rows, { system: "gnpf", code: "", name: "" }])}
                >
                  + Thêm món
                </Button>
              ) : null}
              <Button onClick={submitKeys} disabled={saveKeys.isPending}>
                {saveKeys.isPending ? "Đang lưu…" : "Lưu món trọng điểm"}
              </Button>
            </div>
          </div>
        ) : (keyItems.data ?? []).length === 0 ? (
          <p className="text-sm text-muted">Tháng này chưa chọn món trọng điểm.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {(keyItems.data ?? []).map((k) => (
              <li key={k.itemRef}>
                {k.itemName} <span className="text-muted">({k.itemRef})</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
